import { ApplicationElement, html } from '@beunity/lit-components';
import '@beunity/lit-components/components/grid_list/element.js';
import store from 'store2';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Wrapper for two lists of files/folders
 *
 * @prop layout - Current layout of files/folders in list (options: thumb, list)
 *
 * @slot folders - The upper section, most likely filled with `bu-folder` components
 * @slot folders-title - The title of folders section, defaults to `<h4>Folder</h4>`
 * @slot files - The lower section, most likely filled with `bu-file` components. Can be filled with `bu-folder`s in top level view.
 * @slot files-title - The title of files section, defaults to `<h4>Files</h4>`
 * @slot actions - For additional actions in the left side of header: sort, filter...
 */

export default class FilesWrapper extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    layout: { type: String, reflect: true },
    isTopLevel: { type: Boolean, state: true },
    isThumb: { type: Boolean, state: true },
    foldersCount: { type: Number, state: true },
    filesCount: { type: Number, state: true },
  };

  constructor() {
    super();
    this.storage = store.namespace(this.tagName.toLowerCase());
    this.layout = this.storage.has('layout')
      ? this.storage.get('layout')
      : 'thumb';
  }

  willUpdate(changedProps) {
    super.willUpdate(changedProps);

    if (changedProps.has('layout')) this.handleSlotAndLayoutChanges();
  }

  /** @internal */
  get items() {
    return this.querySelectorAll('bu-folder, bu-file');
  }

  /** @internal */
  get nextLayoutIcon() {
    return this.isThumb ? 'list' : 'grid-2';
  }

  handleToggleLayout() {
    this.layout = { list: 'thumb', thumb: 'list' }[this.layout];
    this.storage.set('layout', this.layout);
  }

  handleSlotAndLayoutChanges(e) {
    // Slot changes only
    if (e) {
      // e.target.name can be: "files" or "folders" (name of the target slot)
      // We set properties foldersCount and filesCount so that we can hide empty sections
      this[`${e.target.name}Count`] = e.target.assignedElements({
        flatten: true,
      }).length;
    }

    // Slot or layout changes
    this.items.forEach(item => (item.layout = this.layout));
    this.isTopLevel = this.items[0]?.isTopLevel; // isTopLevel - will only contain top level folders

    // Optimization: this.layout === 'thumb' (string comparison) would be used many times otherwise
    this.isThumb = this.layout === 'thumb';
  }

  renderSectionHeader() {
    return html`
      <div class="section-table-header">
        <div>${this.t('name')}</div>
        <div>${this.t('owner')}</div>
        <div>${this.t('created_at')}</div>
        <div>${this.t('size')}</div>
      </div>
    `;
  }

  // sectionName options: folders, files
  renderSectionItems(sectionName) {
    return this.isThumb
      ? html`
          <ef-grid-list gap="large" col="2" colxs="3">
            <slot
              name=${sectionName}
              @slotchange=${this.handleSlotAndLayoutChanges}></slot>
          </ef-grid-list>
        `
      : html`
          <ef-grid-list gap="/" col="1">
            <slot
              name=${sectionName}
              @slotchange=${this.handleSlotAndLayoutChanges}></slot>
          </ef-grid-list>
        `;
  }

  // sectionName options: folders, files
  renderSection(sectionName) {
    return html`
      <!-- foldersCount and filesCount are set in handleSlotAndLayoutChanges -->
      <section
        ?hidden=${!this[`${sectionName}Count`]}
        class=${this.isTopLevel ? 'level-top' : 'level-inner'}>
        <slot name="${sectionName}-title">
          <h4 class="bu-title-5">${this.t(sectionName)}</h4>
        </slot>

        ${this.isThumb || this.isTopLevel ? null : this.renderSectionHeader()}
        ${this.renderSectionItems(sectionName)}
      </section>
    `;
  }

  render() {
    return html`
      <header>
        <slot name="actions"></slot>
        <button @click=${this.handleToggleLayout} class="bu-btn btn-naked-dark">
          <bu-icon name=${this.nextLayoutIcon}></bu-icon>
        </button>
      </header>

      ${this.renderSection('folders')} ${this.renderSection('files')}
    `;
  }
}

FilesWrapper.registerTagName('bu-files-wrapper');

customElements.get('bu-files-wrapper') ||
  customElements.define('bu-files-wrapper', FilesWrapper);
